<template>
  <el-container class="chat-page" :class="{ 'mobile-in-chat': configStore.isMobile && activeChat }">
    <el-aside class="page-aside" v-show="!configStore.isMobile || !activeChat">
      <div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <div class="header-tools">
          <el-input class="search-text im-session-search" size="small" placeholder="搜索会话名称" v-model="searchText">
            <i class="el-icon-search el-input__icon" slot="prefix"> </i>
          </el-input>
          <chat-quick-actions
            ref="quickActions"
            :can-create-group="canCreateGroup"
            @select="onSelectQuickAction"
          ></chat-quick-actions>
        </div>
        <div v-if="!configStore.isMobile" class="header-filters">
          <div class="filter-select">
            <span class="filter-select__label">聊天</span>
            <el-select
              v-model="chatTypeFilter"
              size="mini"
              class="filter-select__control"
              popper-class="chat-filter-select-dropdown"
            >
              <el-option
                v-for="item in chatTypeFilterOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
          <div class="filter-select">
            <span class="filter-select__label">状态</span>
            <el-select
              v-model="readStateFilter"
              size="mini"
              class="filter-select__control"
              popper-class="chat-filter-select-dropdown"
            >
              <el-option
                v-for="item in readStateFilterOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>
      <div class="session-list-stage">
        <div v-if="showLoadingPlaceholder" class="chat-loading" role="status" aria-live="polite">
          <div class="chat-loading__card">
            <div class="chat-loading__icon-shell">
              <i class="el-icon-loading chat-loading__icon"></i>
            </div>
            <div class="chat-loading__title">消息接收中...</div>
            <div class="chat-loading__hint">{{ sessionLoadingHint }}</div>
          </div>
        </div>
        <empty-state
          v-else-if="showChats.length === 0"
          class="aside-empty-state"
          icon="el-icon-chat-dot-round"
          :title="sessionEmptyTitle"
          :hint="sessionEmptyHint"
        ></empty-state>
        <virtual-scroller
          v-else
          ref="chatScroller"
          class="scroll-box"
          :items="showChats"
          :item-height="68"
          :item-key-getter="getChatRowKey"
          @reach-bottom="onChatListReachBottom"
        >
          <template v-slot="{ item }">
            <chat-item :chat="item" @click.native="onActiveItem(item)" @delete="onDelItem(item)" @info="onShowInfo(item)"
              @dnd="onDnd(item)" @top="onTop(item)" :active="isActiveChat(item)"></chat-item>
          </template>
        </virtual-scroller>
      </div>
    </el-aside>
    <div class="chat-main-area" v-show="!configStore.isMobile || activeChat">
      <!-- 移动端返回按钮 -->
      <div v-if="configStore.isMobile && activeChat && activeChat.type == 'SYSTEM'" class="mobile-back-header">
        <div class="mobile-back-left">
          <button type="button" class="mobile-back-btn" @click="onMobileBack">
            <i class="el-icon-arrow-left"></i>
          </button>
          <span class="mobile-back-title">{{ activeChat.showName }}</span>
        </div>
      </div>
      <chat-box v-if="activeChat && activeChat.type != 'SYSTEM'" ref="activeChatBox" :chat="activeChat" @back="onMobileBack"></chat-box>
      <chat-system-box v-if="activeChat && activeChat.type == 'SYSTEM'" :chat="activeChat"></chat-system-box>
      <!-- 未选择会话时的空状态 -->
      <empty-state
        v-if="!activeChat"
        class="chat-empty-state"
        icon="el-icon-chat-dot-round"
        title="选择一个会话开始聊天"
        hint="从左侧列表中选择联系人或群组"
      ></empty-state>
    </div>
    <add-friend :dialogVisible="showQuickAddFriend" :quickMode="true" @close="showQuickAddFriend = false"></add-friend>
    <quick-create-group :dialogVisible="showQuickCreateGroup" @close="showQuickCreateGroup = false"></quick-create-group>
  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import ChatSystemBox from "../components/chat/ChatSystemBox.vue";
import ChatQuickActions from "../components/chat/ChatQuickActions.vue";
import VirtualScroller from "../components/common/VirtualScroller.vue";
import AddFriend from "../components/friend/AddFriend.vue";
import QuickCreateGroup from "../components/group/QuickCreateGroup.vue";
import { filterChats } from "../utils/chatSessionFilters.js";
import { isUnavailableGroup, resolveUnavailableGroupNotice } from "../utils/groupAvailability.js";

const SESSION_LOADING_PLACEHOLDER_MIN_VISIBLE_MS = 300;

export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    ChatSystemBox,
    ChatQuickActions,
    VirtualScroller,
    AddFriend,
    QuickCreateGroup
  },
  data() {
    return {
      searchText: "",
      chatTypeFilter: "ALL",
      readStateFilter: "ALL",
      messageContent: "",
      group: {},
      groupMembers: [],
      showQuickAddFriend: false,
      showQuickCreateGroup: false,
      loadingPlaceholderVisible: false,
      loadingPlaceholderHideTimer: null
    }
  },
  methods: {
    getChatRowKey(chat) {
      if (!chat) {
        return '';
      }
      if (chat.type && (chat.targetId !== undefined && chat.targetId !== null)) {
        return `${chat.type}:${chat.targetId}`;
      }
      if (chat.id !== undefined && chat.id !== null) {
        return `chat:${chat.id}`;
      }
      return '';
    },
    isActiveChat(chat) {
      if (!chat || !this.activeChat) {
        return false;
      }
      if (chat === this.activeChat) {
        return true;
      }
      if (chat.type !== this.activeChat.type) {
        return false;
      }
      const chatTargetId = chat.targetId === undefined || chat.targetId === null ? '' : String(chat.targetId);
      const activeTargetId = this.activeChat.targetId === undefined || this.activeChat.targetId === null
        ? ''
        : String(this.activeChat.targetId);
      if (chatTargetId || activeTargetId) {
        return chatTargetId === activeTargetId;
      }
      if (chat.id && this.activeChat.id) {
        return chat.id === this.activeChat.id;
      }
      return chat.showName === this.activeChat.showName;
    },
    onActiveItem(chat) {
      if (!chat || chat.delete) {
        return;
      }
      if (chat.type === 'GROUP') {
        const group = this.groupStore.findGroup(chat.targetId);
        if (isUnavailableGroup(group)) {
          this.chatStore.removeGroupChat(chat.targetId);
          this.$message.warning(resolveUnavailableGroupNotice(group));
          return;
        }
      }
      this.chatStore.setActiveChat(chat);
    },
    onMobileBack() {
      this.chatStore.setActiveChat(null);
    },
    handleEscape() {
      if (this.$refs.quickActions && this.$refs.quickActions.visible) {
        this.$refs.quickActions.close();
        return true;
      }
      if (this.showQuickAddFriend) {
        this.showQuickAddFriend = false;
        return true;
      }
      if (this.showQuickCreateGroup) {
        this.showQuickCreateGroup = false;
        return true;
      }
      if (this.activeChat) {
        this.onMobileBack();
        return true;
      }
      return false;
    },
    onMobileChatMore() {
      if (this.activeChat.type == 'PRIVATE') {
        this.$router.push("/home/friend?id=" + this.activeChat.targetId);
      } else if (this.activeChat.type == 'GROUP') {
        if (!this.groupStore.isGroup(this.activeChat.targetId)) {
          this.$message.error("您已不在群聊中，无法查看群资料");
          return;
        }
        this.$router.push("/home/group?id=" + this.activeChat.targetId);
      }
    },
    onDelItem(chat) {
      this.$confirm(`删除后记录将清空,确认删除与' ${chat.showName}'的聊天 ?`, '删除会话', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (chat.type == 'PRIVATE' || chat.type == 'GROUP') {
            const data = {
              chatId: chat.targetId
            }
            await this.$http({
              url: `/message/${chat.type.toLowerCase()}/deleteChat`,
              method: 'delete',
              data: data
            });
        }
        this.chatStore.removeChat(chat);
      });
    },
    onShowInfo(chat) {
      if (chat.type == 'PRIVATE') {
        this.$router.push("/home/friend?id=" + chat.targetId);
      } else if (chat.type == 'GROUP') {
        if (!this.groupStore.isGroup(chat.targetId)) {
          this.$message.error("您已不在群聊中，无法查看群资料")
          return;
        }
        this.$router.push("/home/group?id=" + chat.targetId);
      }
    },
    onDnd(chat) {
      if (chat.type == 'PRIVATE') {
        this.setFriendDnd(chat, chat.targetId, !this.chatStore.isDnd(chat))
      } else {
        this.setGroupDnd(chat, chat.targetId, !this.chatStore.isDnd(chat))
      }
    },
    setFriendDnd(chat, friendId, isDnd) {
      let formData = {
        friendId: friendId,
        isDnd: isDnd
      }
      this.$http({
        url: '/friend/dnd',
        method: 'put',
        data: formData
      }).then(() => {
        this.chatStore.setDnd(chat, isDnd)
      })
    },
    setGroupDnd(chat, groupId, isDnd) {
      let formData = {
        groupId: groupId,
        isDnd: isDnd
      }
      this.$http({
        url: '/group/dnd',
        method: 'put',
        data: formData
      }).then(() => {
        this.chatStore.setDnd(chat, isDnd)
      })
    },
    onTop(chat) {
      const isTop = !this.chatStore.isTop(chat);

      // 如果是置顶操作，检查数量限制
      if (isTop) {
        const topLimit = this.$permission.getSessionTopLimit();
        const currentTopCount = this.chatStore.topSize();

        if (currentTopCount >= topLimit) {
          this.$message.warning(`会话置顶数量已达上限（${topLimit}个）`);
          return;
        }
      }

      if (chat.type == 'PRIVATE') {
        this.setFriendTop(chat, chat.targetId, isTop)
      } else {
        this.setGroupTop(chat, chat.targetId, isTop)
      }
    },
    setFriendTop(chat, friendId, isTop) {
      let formData = {
        friendId: friendId,
        isTop: isTop
      }
      this.$http({
        url: '/friend/top',
        method: 'put',
        data: formData
      }).then(() => {
        this.chatStore.setTop(chat, isTop)
      })
    },
    setGroupTop(chat, groupId, isTop) {
      let formData = {
        groupId: groupId,
        isTop: isTop
      }
      this.$http({
        url: '/group/top',
        method: 'put',
        data: formData
      }).then(() => {
        this.chatStore.setTop(chat, isTop)
      })
    },
    onSelectQuickAction(action) {
      if (action === 'add-friend') {
        this.showQuickAddFriend = true;
        return;
      }
      if (action === 'create-group') {
        this.showQuickCreateGroup = true;
      }
    },
    getChatSearchText(chat) {
      return chat && chat.searchText ? chat.searchText : '';
    },
    triggerChatListLoadMore() {
      if (this.searchText || this.chatTypeFilter !== 'ALL' || this.readStateFilter !== 'ALL') {
        return;
      }
      if (!this.home || typeof this.home.loadMoreRecentChatsForSessionList !== 'function') {
        return;
      }
      if (!this.home.recentSessionRestoreHasMore || !this.home.recentSessionRestoreCursor) {
        if (typeof this.home.primeRecentChatPaginationState === 'function') {
          this.home.primeRecentChatPaginationState().then(() => {
            if (this.searchText || this.chatTypeFilter !== 'ALL' || this.readStateFilter !== 'ALL') {
              return;
            }
            if (!this.home || this.home.recentSessionBackgroundPromise) {
              return;
            }
            if (!this.home.recentSessionRestoreHasMore || !this.home.recentSessionRestoreCursor) {
              return;
            }
            this.home.loadMoreRecentChatsForSessionList({
              maxPages: 1
            }).catch((error) => {
              console.warn('会话列表滚动加载更多失败', error);
            });
          });
        }
        return;
      }
      if (!this.home.recentSessionRestoreHasMore || !this.home.recentSessionRestoreCursor) {
        return;
      }
      if (this.home.recentSessionBackgroundPromise) {
        return;
      }
      this.home.loadMoreRecentChatsForSessionList({
        maxPages: 1
      });
    },
    getChatScrollerMetrics() {
      const chatScroller = this.$refs.chatScroller;
      const container = chatScroller && chatScroller.$refs ? chatScroller.$refs.container : null;
      if (!container) {
        return null;
      }
      return {
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight
      };
    },
    maybeAutoLoadChatList() {
      if (this.searchText || this.chatTypeFilter !== 'ALL' || this.readStateFilter !== 'ALL') {
        return;
      }
      if (!this.home || this.home.recentSessionBackgroundPromise) {
        return;
      }
      this.$nextTick(() => {
        const metrics = this.getChatScrollerMetrics();
        if (!metrics) {
          return;
        }
        if (metrics.scrollHeight > metrics.clientHeight + 4) {
          return;
        }
        this.triggerChatListLoadMore();
      });
    },
    onChatListReachBottom() {
      this.triggerChatListLoadMore();
    }
  },
  computed: {
    home() {
      return this.$parent && typeof this.$parent.loadMoreRecentChatsForSessionList === 'function'
        ? this.$parent
        : null;
    },
    activeChat() {
      return this.chatStore.activeChat;
    },
    loading() {
      return this.chatStore.loading
    },
    canCreateGroup() {
      return this.$permission.canCreateGroup();
    },
    chatTypeFilterOptions() {
      return [
        { label: '全部', value: 'ALL' },
        { label: '单聊', value: 'PRIVATE' },
        { label: '群聊', value: 'GROUP' }
      ];
    },
    readStateFilterOptions() {
      return [
        { label: '全部', value: 'ALL' },
        { label: '已读', value: 'READ' },
        { label: '未读', value: 'UNREAD' }
      ];
    },
    showChats() {
      return filterChats(this.baseChats, {
        searchText: this.searchText,
        chatType: this.chatTypeFilter,
        readState: this.readStateFilter,
        getSearchText: this.getChatSearchText
      });
    },
    baseChats() {
      return this.chatStore.findChats();
    },
    hasAnyChats() {
      return this.baseChats.length > 0;
    },
    connectionPhase() {
      return this.configStore.connection && this.configStore.connection.phase
        ? this.configStore.connection.phase
        : 'IDLE';
    },
    connectionHint() {
      return this.configStore.connection && this.configStore.connection.hint
        ? this.configStore.connection.hint
        : '';
    },
    hasPendingConnectionPhase() {
      return ['CONNECTING', 'SYNCING', 'RECONNECTING'].includes(this.connectionPhase);
    },
    showLoadingPlaceholderNow() {
      return !this.hasAnyChats && (this.loading || this.hasPendingConnectionPhase);
    },
    showLoadingPlaceholder() {
      return !this.hasAnyChats && (this.showLoadingPlaceholderNow || this.loadingPlaceholderVisible);
    },
    sessionLoadingHint() {
      if (this.connectionHint) {
        return this.connectionHint;
      }
      return '正在同步最近会话与消息，请稍候。';
    },
    sessionEmptyTitle() {
      if (this.searchText) {
        return '没有找到匹配会话';
      }
      if (this.chatTypeFilter === 'PRIVATE') {
        return this.readStateFilter === 'UNREAD' ? '暂无未读单聊' : '暂无单聊会话';
      }
      if (this.chatTypeFilter === 'GROUP') {
        return this.readStateFilter === 'UNREAD' ? '暂无未读群聊' : '暂无群聊会话';
      }
      return this.readStateFilter === 'UNREAD' ? '暂无未读会话' : '暂无会话';
    },
    sessionEmptyHint() {
      if (this.searchText) {
        return '试试调整会话名称关键词，或切换聊天类型与已读状态筛选。';
      }
      if (this.readStateFilter === 'UNREAD') {
        return '当前筛选下的会话都已读，或暂时还没有新消息。';
      }
      return '新的聊天会话会在这里出现。';
    }
  },
  watch: {
    showLoadingPlaceholderNow: {
      immediate: true,
      handler(newVal, oldVal) {
        if (this.loadingPlaceholderHideTimer) {
          clearTimeout(this.loadingPlaceholderHideTimer);
          this.loadingPlaceholderHideTimer = null;
        }
        if (newVal) {
          this.loadingPlaceholderVisible = true;
          return;
        }
        if (!oldVal) {
          return;
        }
        this.loadingPlaceholderHideTimer = setTimeout(() => {
          this.loadingPlaceholderHideTimer = null;
          this.loadingPlaceholderVisible = false;
        }, SESSION_LOADING_PLACEHOLDER_MIN_VISIBLE_MS);
      }
    },
    'showChats.length'() {
      this.maybeAutoLoadChatList();
    },
    searchText() {
      this.maybeAutoLoadChatList();
    },
    chatTypeFilter() {
      this.maybeAutoLoadChatList();
    },
    readStateFilter() {
      this.maybeAutoLoadChatList();
    }
  },
  mounted() {
    this.maybeAutoLoadChatList();
  },
  beforeDestroy() {
    if (this.loadingPlaceholderHideTimer) {
      clearTimeout(this.loadingPlaceholderHideTimer);
      this.loadingPlaceholderHideTimer = null;
    }
  }
}
</script>

<style lang="scss">
@import "../assets/style/responsive";

.chat-page {
  height: 100%;
  min-height: 0;

  .page-aside {
    width: clamp(280px, 28vw, 312px);
    min-width: 0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    background: var(--im-session-bg);
    background-image: none;
    border-right: 1px solid var(--im-surface-panel-border);

    @include mobile {
      width: 100%;
      flex: 1 1 auto;
      border-right: none;
    }
  }

  // chat-main-area 基础样式（替代 el-container）
  .chat-main-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    min-width: 0;
    background: var(--im-chat-bg);

    > .chat-box,
    > .chat-system-box,
    > .chat-empty-state {
      flex: 1;
      min-height: 0;
      min-width: 0;
    }
  }

  // 移动端进入会话后：chat-main-area 占满全屏，覆盖 tab bar
  &.mobile-in-chat {
    .chat-main-area {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 200;
      background: var(--im-chat-bg);
      padding-top: env(safe-area-inset-top, 0px);
      box-sizing: border-box;

      // ChatBox 组件填满剩余空间
      > .chat-box,
      > .chat-system-box {
        flex: 1;
        min-height: 0;
        height: auto;
        overflow: hidden;
      }
    }
  }

  .chat-empty-state {
    background: var(--im-chat-bg);
    user-select: none;
  }

  .mobile-back-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 52px;
    padding: 0 12px;
    background: var(--im-chat-bg);
    border-bottom: var(--im-border);
    flex-shrink: 0;
    position: relative;
    z-index: 3;

    .mobile-back-left {
      display: flex;
      align-items: center;
      min-width: 0;
      flex: 1;
      gap: 8px;
    }

    .mobile-back-btn,
    .mobile-back-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: none;
      border-radius: 10px;
      background: transparent;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
      flex-shrink: 0;

      i {
        font-size: 16px;
      }
    }

    .mobile-back-btn {
      color: var(--im-color-primary);
      background: var(--im-control-soft-bg);
      border: 1px solid var(--im-control-border);

      &:hover {
        background: var(--im-background-active-dark);
        border-color: rgba(var(--im-primary-rgb), 0.18);
        transform: scale(1.05);
      }

      &:active {
        transform: scale(0.95);
      }
    }

    .mobile-back-title {
      font-size: var(--im-font-size-large);
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

  }

  .header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--im-session-bg);
    background-image: none;
    border-bottom: 1px solid rgba(var(--im-black-rgb), 0.05);

    .header-tools {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
    }

    .search-text {
      flex: 1;
      min-width: 0;

      .el-input__inner {
        height: 36px;
        line-height: 36px;
        border-radius: 10px;
        padding-left: 38px;
        padding-right: 11px;
      }

      .el-input__prefix {
        left: 10px;
      }

      .el-input__icon {
        width: 16px;
        height: 16px;
        font-size: 13px;
      }
    }

    .header-filters {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 9px;
      min-width: 0;
      flex-wrap: nowrap;
    }

    .filter-select {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      min-width: 0;
      box-sizing: border-box;
      height: 36px;
      padding: 0 8px 0 10px;
      border-radius: 10px;
      border: 1px solid rgba(var(--im-black-rgb), 0.06);
      background: var(--im-control-soft-bg);
    }

    .filter-select__label {
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 600;
      color: var(--im-text-color-secondary);
      line-height: 1;
    }

    .filter-select__control {
      width: 84px;
      flex-shrink: 0;

      .el-input__inner {
        height: 32px;
        padding-left: 0;
        padding-right: 20px;
        border: none;
        border-radius: 0;
        background: transparent;
        color: var(--im-text-color);
        font-size: 12px;
        font-weight: 500;
        box-shadow: none;
      }

      .el-input__icon {
        line-height: 32px;
        color: var(--im-text-color-light);
      }

      .el-input.is-focus .el-input__inner,
      .el-input__inner:focus {
        border: none;
        background: transparent;
        box-shadow: none;
      }
    }
  }

  .chat-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 0;
    padding: 24px 14px;
    background-color: var(--im-session-bg);
    background-image: none;
    text-align: center;

    .chat-loading__card {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 9px;
      max-width: 232px;
    }

    .chat-loading__icon-shell {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: rgba(var(--im-primary-rgb), 0.08);
      color: var(--im-color-primary);
    }

    .chat-loading__icon {
      font-size: 18px;
      line-height: 1;
      animation: im-session-status-spin 0.9s linear infinite;
    }

    .chat-loading__title {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      color: var(--im-text-color-secondary);
    }

    .chat-loading__hint {
      font-size: 12px;
      line-height: 1.5;
      color: var(--im-text-color-light);
    }
  }

  .session-list-stage {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .chat-items {
    flex: 1;
  }

  .scroll-box {
    flex: 1;
    background: var(--im-session-bg);
    background-image: none;
  }

  .aside-empty-state {
    background: var(--im-session-bg);
  }
}

.chat-filter-select-dropdown {
  .el-select-dropdown__item {
    font-size: 12px;
  }
}

@keyframes im-session-status-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
